import { useSnackbarStore } from '~/stores/snackbar'

export type ExtendedEvent = Event & { dataTransfer: DataTransfer } & {
  target: HTMLInputElement
}
type BlobType = null | Blob
type PictureType = null | string | ArrayBuffer
type ImageObjType = null | HTMLImageElement

export interface PictureData {
  picture: PictureType
  imgObj: ImageObjType
  blob: BlobType
}

/**
 * For uploading pictures.
 *
 * @param maxPictureSize - defaults to ~50Mb
 * @returns
 */
export function usePictureUploadUtils(maxPictureSize = 50000000) {
  const { $pinia } = useNuxtApp()
  const { t } = useI18n()

  const picture = ref<PictureType>(null)
  const imgObj = ref<ImageObjType>(null)
  const blob = ref<BlobType>(null)
  const error = ref<boolean>(false)
  const uuid = useId()

  const { CREATE: CREATE_SNACKBAR } = useSnackbarStore($pinia)

  function reset() {
    picture.value = null
    imgObj.value = null
    blob.value = null
  }

  function addPicture() {
    reset()

    const el = window.document.getElementById(uuid)

    if (el) {
      ;(el as HTMLInputElement).value = ''
      el.click()
    }
  }

  async function preProcessPicture(
    e: ExtendedEvent,
  ): Promise<string | undefined> {
    reset()
    await nextTick()
    const targetFiles = e.target.files || e.dataTransfer.files

    if (targetFiles) blob.value = targetFiles[0] ?? null
    return await setupPicture()
  }

  function setupPicture(): Promise<string | undefined> {
    return new Promise((resolve, reject) => {
      if (!checkPictureSize()) {
        error.value = Boolean(blob.value)
        reset()
        reject(new Error('Picture size is too big.'))
        return
      }

      const reader = new FileReader()

      error.value = false
      reader.onload = (e) => {
        const img = new Image()

        img.onload = () => {
          if (e.target) picture.value = e.target.result as string
          imgObj.value = img
        }
        if (e.target) {
          img.src = e.target.result as string
          resolve(img.src)
        }
      }

      if (blob.value) reader.readAsDataURL(blob.value)
    })
  }

  function checkPictureSize(): boolean {
    if (!blob.value || blob.value.size / maxPictureSize >= 1) {
      CREATE_SNACKBAR({
        props: {
          text: t('shared.pictureUploadCard00'),
          icon: 'fas fa-bug',
        },
      })
      return false
    }
    return true
  }

  return {
    addPicture,
    picture,
    preProcessPicture,
    reset,
    uuid,
  }
}
